import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { sheetFetch } from '@/lib/sheetFetch'
import { DONATION_STATUS_LABELS } from '@/lib/fundraising-labels'

interface DuplicateMatch {
  id: number
  amount_formatted: string
  date: string
  status: string
  campaign_name: string | null
}

interface DuplicateWarningDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  duplicates: DuplicateMatch[]
  donationParams: Record<string, unknown>
  // Owning-team choice (US3) — forwarded so the force-create retry files the
  // donation under the team the user selected, not their primary team.
  teamId?: string
  // 201: parent closes the sheet and refreshes the index list.
  onCreated: () => void
  // 422: parent surfaces validation errors inline rather than the dialog
  // silently closing on a failed force-create.
  onErrors: (errors: Record<string, string>) => void
}

export function DuplicateWarningDialog({
  open,
  onOpenChange,
  duplicates,
  donationParams,
  teamId,
  onCreated,
  onErrors,
}: DuplicateWarningDialogProps) {
  const [processing, setProcessing] = useState(false)

  // Force-create re-enters the same JSON contract as the initial submit, this
  // time with skip_duplicate_check so the server bypasses detection. sheetFetch
  // (not Inertia useForm) keeps a post-bypass validation failure visible.
  async function handleForceCreate() {
    setProcessing(true)
    try {
      const response = await sheetFetch('/fundraising/donations', {
        method: 'POST',
        body: { donation: donationParams, team_id: teamId ?? '', skip_duplicate_check: true },
      })
      if (response.status === 201) {
        onCreated()
      } else if (response.status === 422) {
        const json = (await response.json()) as { errors?: Record<string, string> }
        onErrors(json.errors ?? {})
      } else {
        onErrors({ base: 'Une erreur est survenue.' })
      }
    } catch {
      onErrors({ base: 'Impossible de creer le don.' })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Doublons potentiels detectes
          </DialogTitle>
          <DialogDescription>
            Un ou plusieurs dons similaires existent deja. Verifiez avant de continuer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {duplicates.map((dup) => (
            <div
              key={dup.id}
              className="flex items-center justify-between rounded-md border p-3 text-sm"
            >
              <div className="space-y-0.5">
                <p className="font-medium tabular-nums">{dup.amount_formatted}</p>
                <p className="text-muted-foreground">
                  {new Date(dup.date).toLocaleDateString('fr-FR')}
                  {dup.campaign_name && ` — ${dup.campaign_name}`}
                </p>
              </div>
              <span className="text-xs text-muted-foreground">
                {typeof dup.status === 'object' ? dup.status.label : (DONATION_STATUS_LABELS[dup.status] ?? dup.status)}
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleForceCreate} disabled={processing}>
            {processing ? 'Creation...' : 'Creer quand meme'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
